import { interpolateNumber } from 'd3-interpolate';

export interface ValueWithInterpolation {
    value: number;
    isInterpolated: boolean;
}

export function isValueWithInterpolation(
    value: unknown
): value is ValueWithInterpolation {
    const intVal = value as ValueWithInterpolation;
    return (
        intVal &&
        intVal.isInterpolated !== undefined &&
        intVal.value !== undefined
    );
}

export const toValueWithInterpolation: (
    value: number | undefined,
    isInterpolated?: boolean
) => ValueWithInterpolation | undefined = (value, isInterpolated = false) =>
    value === undefined
        ? undefined
        : {
              value: value,
              isInterpolated: isInterpolated,
          };

export const getInterpolatedValue: (
    v?: ValueWithInterpolation
) => number | undefined = (v) => v?.value;

export const getActualValue: (
    v?: ValueWithInterpolation
) => number | undefined = getInterpolatedValue;

/*
 * Note that this class assumes we are working backwards through the time series, as in fillsGraphData
 */
export class InterpolationContext<T extends { time: number }> {
    public constructor(private direction: 'backward' | 'forward') {}

    /*
     * For each field, we record the index of the last item that had a value
     */
    private lastValueIndexByField: {
        [fieldName: string]: number;
    } = {};

    public interpolateLinear(
        data: T[],
        fields: (keyof T)[],
        currentIndex: number
    ) {
        for (const field of fields) {
            this.interpolateLinearField(data, field, currentIndex);
        }
    }

    public interpolateStep(
        data: T[],
        fields: (keyof T)[],
        currentIndex: number,
        targetInterpolationIndex: number
    ) {
        for (const field of fields) {
            this.interpolateStepField(
                data,
                field,
                currentIndex,
                targetInterpolationIndex
            );
        }
    }

    private interpolateLinearField(
        data: T[],
        field: keyof T,
        currentIndex: number
    ) {
        const currentItem = data[currentIndex];
        const currentValue = this.getInterpolatedValue(currentItem, field);

        const lastValueIndex = this.lastValueIndexByField[field as string];

        if (currentValue !== undefined && lastValueIndex !== undefined) {
            const previousItem =
                this.direction === 'backward'
                    ? data[currentIndex + 1]
                    : data[currentIndex - 1];
            const previousValue = this.getInterpolatedValue(
                previousItem,
                field
            );
            if (previousValue === undefined) {
                this.fillGap(data, field, currentIndex, lastValueIndex);
            }
        }

        if (currentValue !== undefined) {
            this.recordLastValueIndex(field, currentIndex);
        }
    }

    private interpolateStepField(
        data: T[],
        field: keyof T,
        currentIndex: number,
        targetInterpolationIndex: number
    ) {
        if (this.direction === 'backward') {
            this.interpolateStepFieldBackwards(
                data,
                field,
                currentIndex,
                targetInterpolationIndex
            );
        } else {
            this.interpolateStepFieldForwards(data, field, currentIndex);
        }
    }

    private interpolateStepFieldBackwards(
        data: T[],
        field: keyof T,
        currentIndex: number,
        targetInterpolationIndex: number
    ) {
        const currentItem = data[currentIndex];
        const currentValue = this.getInterpolatedValue(currentItem, field);

        if (currentValue === undefined) {
            return;
        }

        for (let i = currentIndex + 1; i <= targetInterpolationIndex; i++) {
            const previousItem = data[i];
            const previousValue = this.getInterpolatedValue(
                previousItem,
                field
            );

            if (previousValue !== undefined) {
                return;
            }

            this.setInterpolatedValue(previousItem, field, currentValue.value);
        }
    }

    private interpolateStepFieldForwards(
        data: T[],
        field: keyof T,
        currentIndex: number
    ) {
        const currentItem = data[currentIndex];
        const currentValue = this.getInterpolatedValue(currentItem, field);

        if (currentValue !== undefined) {
            this.recordLastValueIndex(field, currentIndex);
            return;
        }

        const lastValueIndex = this.lastValueIndexByField[field as string];

        if (lastValueIndex === undefined) {
            return;
        }

        const lastItem = data[lastValueIndex];
        const lastValue = this.getInterpolatedValue(lastItem, field);

        this.setInterpolatedValue(currentItem, field, lastValue.value);
    }

    private recordLastValueIndex(fieldName: keyof T, index: number) {
        this.lastValueIndexByField[fieldName as string] = index;
    }

    private fillGap(
        data: T[],
        field: keyof T,
        currentIndex: number,
        lastValueIndex: number
    ) {
        const current: T = data[currentIndex];
        const currentValue = this.getInterpolatedValue(current, field);

        const previousWithValue: T = data[lastValueIndex];
        const previousValue = this.getInterpolatedValue(
            previousWithValue,
            field
        );

        const interpolation = interpolateNumber(
            previousValue.value!,
            currentValue.value!
        );

        const steps = Math.abs(lastValueIndex - currentIndex);
        let interpolatingIndex =
            this.direction === 'backward'
                ? lastValueIndex - 1
                : lastValueIndex + 1;

        const contFilling = () =>
            this.direction === 'backward'
                ? interpolatingIndex > currentIndex
                : interpolatingIndex < currentIndex;

        while (contFilling()) {
            const prm = Math.abs(lastValueIndex - interpolatingIndex) / steps;
            const value = interpolation(prm);

            const interpolatingItem = data[interpolatingIndex];
            this.setInterpolatedValue(interpolatingItem, field, value);

            interpolatingIndex =
                this.direction === 'backward'
                    ? interpolatingIndex - 1
                    : interpolatingIndex + 1;
        }
    }

    private getInterpolatedValue(
        target: T,
        fieldName: keyof T
    ): ValueWithInterpolation {
        return target[fieldName] as unknown as ValueWithInterpolation;
    }

    private setInterpolatedValue(
        target: T,
        fieldName: keyof T,
        interpolatedValue: number
    ) {
        target[fieldName] = toValueWithInterpolation(
            interpolatedValue,
            true
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ) as any;
    }
}
